import React, { useState } from 'react';
import useScrollReveal, { useStaggerReveal } from '../hooks/useScrollReveal';

const NCARVault = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);
  const [headerRef, headerVisible] = useScrollReveal();
  const [statsRef, statsVisible] = useScrollReveal();
  const [pubsRef, , getPubItemProps] = useStaggerReveal(12, { staggerDelay: 0.08 });
  const [ctaRef, ctaVisible] = useScrollReveal();

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'publication', label: 'Publications' },
    { id: 'presentation', label: 'Presentations' },
  ];

  const publications = [
    {
      id: 1,
      type: 'publication',
      title: 'Ensemble Methods for Parameter Estimation of WRF-Hydro',
      authors: 'Rafieeinasab, A., Fienen, M., Omani, N., Srivastava, I., Dugger, A.',
      journal: 'Water Resources Research',
      year: '2025',
      citations: 6,
      abstract: 'Traditional parameter estimation techniques for landscape models have focused on objective functions based on popular model performance criteria such as Nash-Sutcliffe and Kling-Gupta Efficiencies. We illustrate this approach using WRF-Hydro and a cutout of the National Water Model, comparing between DDS and iES using the same parameterization and objective function.',
      tags: ['WRF-Hydro', 'Parameter Estimation', 'Ensemble Methods', 'National Water Model'],
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50/60',
      borderColor: 'border-teal-200',
    },
    {
      id: 2,
      type: 'publication',
      title: 'A WRF-Hydro-based Retrospective Simulation of Water Resources for US Integrated Water Availability Assessment',
      authors: 'Rafieeinasab, A., Sampson, K., Srivastava, I., Dugger, A.',
      journal: 'Hydrology and Earth System Sciences',
      year: '2024',
      citations: 2,
      abstract: 'This simulation was developed to provide water budget estimates for the period 10/1/2009 to 9/30/2021 using the bias adjusted version of the CONUS404 atmospheric forcings dataset at the NHDPlus version 2 spatial resolution.',
      tags: ['WRF-Hydro', 'Water Resources', 'CONUS404', 'Retrospective Simulation'],
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50/60',
      borderColor: 'border-blue-200',
    },
    {
      id: 3,
      type: 'publication',
      title: 'CONUS404 Climate Forcing Variable Subset for Hydrologic Models, 1979-2022: Downscaled to 1km and Bias-adjusted',
      authors: 'Zhang, Y., Grim, J., Cabell, R., Srivastava, I., Gochis, D., Prein, A., et al.',
      journal: 'USGS Data Release',
      year: '2024',
      citations: 1,
      abstract: 'This metadata record serves as documentation for the bias adjusted version of the CONUS404 (CONtiguous United States for 40-years at 4-kilometer grid spacing) atmospheric forcing dataset. The CONUS404 BA outputs are approximately 65 TB in size and contain downscaled hourly data for U component of wind, V component of wind, temperature, water vapor mixing ratio, surface pressure, and accumulated precipitation variables.',
      tags: ['CONUS404', 'Climate Data', '65TB Dataset', 'Bias Adjustment'],
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50/60',
      borderColor: 'border-purple-200',
    },
    {
      id: 10,
      type: 'publication',
      title: 'Challenges and Insights in Transitioning Hydrologic Data Assimilation from Regional to CONUS Scale',
      authors: 'Srivastava, I., et al.',
      journal: 'Conference Proceedings',
      year: '2025',
      citations: 0,
      abstract: 'Explores the technical challenges encountered when scaling hydrologic data assimilation from regional domains to the full contiguous United States (CONUS), including computational constraints, data heterogeneity, and parameter estimation at scale.',
      tags: ['Data Assimilation', 'WRF-Hydro', 'CONUS Scale', 'Hydrology'],
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-50/60',
      borderColor: 'border-emerald-200',
    },
    {
      id: 11,
      type: 'publication',
      title: 'Application of WRF-Hydro Modeling System for CONUS at NHDPlus v2',
      authors: 'Rafieeinasab, A., Srivastava, I., et al.',
      journal: 'USGS Data Release',
      year: '2024',
      citations: 0,
      abstract: 'USGS data release documenting the application of the WRF-Hydro modeling system configured at NHDPlus version 2 spatial resolution for the contiguous United States, providing calibrated hydrological simulations for water resource assessment.',
      tags: ['WRF-Hydro', 'NHDPlus v2', 'USGS', 'CONUS'],
      color: 'from-sky-500 to-blue-500',
      bgColor: 'bg-sky-50/60',
      borderColor: 'border-sky-200',
    },
    {
      id: 12,
      type: 'publication',
      title: 'Uncertainty Analysis and Parameter Estimation for Landscape Models using iES',
      authors: 'Rafieeinasab, A., Fienen, M., Srivastava, I., et al.',
      journal: 'Research Publication',
      year: '2024',
      citations: 0,
      abstract: 'Presents uncertainty analysis and parameter estimation approaches for landscape models using iterative Ensemble Smoother (iES), demonstrating methods for quantifying parameter uncertainty in complex hydrological simulations.',
      tags: ['Parameter Estimation', 'iES', 'Uncertainty Analysis', 'Landscape Models'],
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-50/60',
      borderColor: 'border-violet-200',
    },
    {
      id: 4,
      type: 'presentation',
      title: 'METplus in Action: Bridging Research and Operations for Model Verification',
      authors: 'Srivastava, I., et al.',
      conference: '106th AMS Annual Meeting',
      year: '2026',
      abstract: 'The enhanced Model Evaluation Tools (METplus) system provides a community-driven framework that supports robust, flexible, and scalable model verification across a wide range of applications. Several organizations within NOAA, the United States Air Force (USAF), the Naval Research Laboratory (NRL), and international partnerships have begun adopting the tools for operational use.',
      tags: ['METplus', 'Model Verification', 'NOAA', 'USAF'],
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50/60',
      borderColor: 'border-orange-200',
    },
    {
      id: 5,
      type: 'presentation',
      title: 'Integration of Probabilistic Precipitation Forecasts into NOAA\'s National Water Model: Hurricane Beryl Case Study',
      authors: 'Srivastava, I., et al.',
      conference: '39HYDRO AMS Conference',
      year: '2025',
      abstract: 'The accuracy of forecasting extreme hydrologic events, including flash floods and long-duration floods, is significantly influenced by the skill of quantitative precipitation forecasts (QPFs). This work seeks to quantify the impact of probabilistic information from NBM on ensemble streamflow forecasting, capturing Hurricane Beryl\'s landfall in the South Central region of the United States.',
      tags: ['National Water Model', 'Hurricane Beryl', 'Probabilistic Forecasting', 'Flood Prediction'],
      color: 'from-rose-500 to-red-500',
      bgColor: 'bg-rose-50/60',
      borderColor: 'border-rose-200',
    },
    {
      id: 6,
      type: 'presentation',
      title: 'Enhancing Streamflow Drought Simulation through Calibration and Surface Data-Assimilation for the US Drought Monitor',
      authors: 'Srivastava, I., et al.',
      conference: 'AMS Annual Meeting',
      year: '2025',
      abstract: 'Droughts are considered to be the most detrimental natural hazard of the 20th century. We present a hydrologic modeling framework that leverages the WRF-Hydro routing structure, based on the NOAA National Water Model configuration, combined with NASA Land Information System data-assimilation capabilities.',
      tags: ['Drought Monitor', 'WRF-Hydro', 'Data Assimilation', 'NASA LIS'],
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-50/60',
      borderColor: 'border-amber-200',
    },
    {
      id: 7,
      type: 'presentation',
      title: 'Ingesting the Operational National Blend of Models into the Operational NOAA National Water Model',
      authors: 'Mazrooei, A., Zhang, Y., Srivastava, I., Cabell, R., McCluskey, A., Gochis, D.',
      conference: 'AGU Fall Meeting',
      year: '2023',
      abstract: 'Skillful forecasting of extreme hydrologic events such as flash floods and long-duration floods, are highly affected by the skill of quantitative precipitation forecasts (QPFs). The operational National Water Model (NWM) produces medium-range hydrologic forecasts over 3 million stream channels over the OCONUS.',
      tags: ['National Water Model', 'NBM', 'NOAA', 'Hydrologic Forecasting'],
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50/60',
      borderColor: 'border-green-200',
    },
    {
      id: 8,
      type: 'presentation',
      title: 'Cloud-Based Online Training Platform for WRF-HYDRO Model',
      authors: 'Srivastava, I., Cabell, R., FitzGerald, K., McAllister, M.',
      conference: '102nd Annual AMS Meeting',
      year: '2022',
      abstract: 'During the COVID-19 pandemic, in-person training sessions were no longer feasible. The new cloud-based training platform is designed as an end-to-end SaaS system to conduct interactive WRF-Hydro online training for hundreds of users simultaneously. The system was successfully used for AGU, AMS, and CUAHSI training sessions with 150+ simultaneous users.',
      tags: ['AWS', 'Cloud Platform', 'WRF-Hydro', 'SaaS'],
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-50/60',
      borderColor: 'border-cyan-200',
    },
    {
      id: 9,
      type: 'presentation',
      title: 'Application of Machine Learning in NCAR\'s Runway Friction and Closure Prediction System (RFCPS)',
      authors: 'Srivastava, I., Linden, S., Brummet, T., Wiener, G., Petzke, W.',
      conference: '99th Annual AMS Meeting',
      year: '2019',
      abstract: 'The safety and efficiency of airport and flight operations is significantly disrupted during adverse winter storms. The RFCPS developed by NCAR uses a backend forecast engine combined with a machine learning module and rules of practice to predict runway friction and closure times for Minneapolis-Saint Paul and Denver International airports.',
      tags: ['Machine Learning', 'RFCPS', 'Airport Safety', 'MSP', 'DIA'],
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50/60',
      borderColor: 'border-indigo-200',
    },
  ];

  const filteredPublications =
    activeFilter === 'all'
      ? publications
      : publications.filter((pub) => pub.type === activeFilter);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'publication':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'presentation':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section id="ncar" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-gradient-to-br from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 text-sm font-medium mb-4">
            Research Archive
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">NCAR</span> Legacy Vault
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            9 years of research contributions to atmospheric science, hydrological modeling, and applied ML
          </p>
        </div>

        {/* Stats Bar */}
        <div ref={statsRef} className="grid grid-cols-3 gap-4 mb-12">
          {[
            { value: '12', label: 'Publications', color: 'from-teal-500 to-cyan-500' },
            { value: '9', label: 'Citations', color: 'from-purple-500 to-pink-500' },
            { value: '8+', label: 'Conferences', color: 'from-orange-500 to-amber-500' },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className={`text-center p-4 rounded-2xl bg-white/50 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'}`}
              style={{ transitionDelay: statsVisible ? `${i * 0.1}s` : '0s' }}
            >
              <div className={`text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Publications Grid */}
        <div ref={pubsRef} className="space-y-4">
          {filteredPublications.map((pub, index) => {
            const itemProps = getPubItemProps(index);
            return (
            <div
              key={pub.id}
              className={`${pub.bgColor} rounded-2xl border ${pub.borderColor} shadow-sm hover:shadow-lg cursor-pointer overflow-hidden ${itemProps.className}`}
              style={itemProps.style}
              onClick={() => setExpandedItem(expandedItem === pub.id ? null : pub.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${pub.color} text-white`}>
                        {getTypeIcon(pub.type)}
                        <span className="ml-1.5 capitalize">{pub.type}</span>
                      </span>
                      <span className="text-gray-500 text-sm font-medium">{pub.year}</span>
                      {pub.citations > 0 && (
                        <span className="text-xs text-gray-500 flex items-center bg-gray-100 px-2 py-1 rounded-full">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          {pub.citations} citations
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {pub.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">{pub.authors}</p>
                    <p className="text-xs text-gray-400">
                      {pub.journal || pub.conference}
                    </p>
                  </div>
                  <div className={`transition-transform duration-300 ${expandedItem === pub.id ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  expandedItem === pub.id ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className={`px-6 pb-6 pt-2 border-t ${pub.borderColor}`}>
                  <h4 className={`text-sm font-semibold bg-gradient-to-r ${pub.color} bg-clip-text text-transparent mb-2`}>Abstract</h4>
                  <p className="text-sm text-gray-600 mb-4">{pub.abstract}</p>
                  <div className="flex flex-wrap gap-2">
                    {pub.tags.map((tag) => (
                      <span key={tag} className={`px-2.5 py-1 rounded-full text-xs font-medium ${pub.bgColor} text-gray-700 border ${pub.borderColor}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>

        {/* View More Link */}
        <div
          ref={ctaRef}
          className={`text-center mt-10 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <a
            href="https://scholar.google.com/citations?user=xY8c1x8AAAAJ&hl=en"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 rounded-full font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
          >
            <span>View All on Google Scholar</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default NCARVault;
